import { Component, Element, Event, EventEmitter, Host, Prop, State, h } from '@stencil/core';
import { getAPI, type UserAPI } from '@sydle/ui-api';
import { getConfig } from '@sydle/ui-utils';
import { type BlogConfig } from 'packages/sydle-ui-blog/src/types/blog';
import { syQuerySelector } from '../../../utils/dom.utils';
import { newsletterEvent } from '../../../utils/tracking-utils';

@Component({
  tag: 'sy-blog-cta-newsletter',
  styleUrl: './scss/index.scss',
  shadow: true,
})
export class SyBlogCtaNewsletter {
  @Element() el: HTMLSyBlogCtaNewsletterElement;

  /**
   * CTA text
   */
  @Prop() text: string;

  /**
   * CTA button content
   */
  @Prop() buttonContent: string;

  /**
   * Success dialog title
   */
  @Prop() successDialogTitle: string;

  /**
   * Success dialog message
   */
  @Prop() successDialogMessage: string;

  /**
   * Already subscribed dialog title
   */
  @Prop() alreadySubscribedDialogTitle: string;

  /**
   * Already subscribed dialog message
   */
  @Prop() alreadySubscribedDialogMessage: string;

  /**
   * Unselected newsletter title
   */
  @Prop() unselectedNewsletterTitle: string;

  /**
   * Unselected newsletter message
   */
  @Prop() unselectedNewsletterMessage: string;

  /**
   * Emitted after CTA button is clicked
   */
  @Event({ bubbles: true, composed: true }) sySignInWallOpen: EventEmitter<void>;

  @State() subscribeArray: string[] = [];
  @State() consentList: Array<{ identifier: string; _id: string }>;
  @State() individualSubscribe: boolean | undefined;

  private handleToggleCheckboxConsent(id: string) {
    if (this.subscribeArray.includes(id)) {
      this.subscribeArray.splice(this.subscribeArray.indexOf(id), 1);
    } else {
      this.subscribeArray.push(id);
    }
  }

  private subscribeToNewsletter(items?: any): void {
    const modal = syQuerySelector('sy-blog-alert-modal') as HTMLSyBlogAlertModalElement;
    if (!this.individualSubscribe) {
      items = this.consentList?.map((consent) => consent._id);
    }
    items = items.map((el: any) => ({ _id: el }));
    window.blogFacade
      .registerUserConsents({ consents: items })
      .then((_output) => _output.data?.newSubscriptions)
      .then((newSubscriptions) => {
        if (newSubscriptions && newSubscriptions.length > 0) {
          modal.dialogTitle = this.successDialogTitle;
          modal.dialogMessage = this.successDialogMessage;

          newSubscriptions.forEach((consent) => {
            newsletterEvent(consent.identifier);
          });
        } else {
          modal.dialogTitle = this.alreadySubscribedDialogTitle;
          modal.dialogMessage = this.alreadySubscribedDialogMessage;
        }
        modal.open();
      });
  }

  private handleConsentsSubscribe = async () => {
    if (this.individualSubscribe && JSON.stringify(this.subscribeArray) === JSON.stringify([])) {
      const modal = syQuerySelector('sy-blog-alert-modal') as HTMLSyBlogAlertModalElement;
      modal.dialogTitle = this.unselectedNewsletterTitle;
      modal.dialogMessage = this.unselectedNewsletterMessage;
      modal.open();
    } else {
      if (getAPI<UserAPI>('userAPI')?.getUser()) {
        this.subscribeToNewsletter(this.subscribeArray);
      } else {
        sessionStorage.setItem('subscribeLogin', JSON.stringify(this.subscribeArray));
        this.sySignInWallOpen.emit();
      }
    }
  };

  componentDidLoad() {
    if (sessionStorage.getItem('subscribeLogin')) {
      if (getAPI<UserAPI>('userAPI')?.getUser()) {
        this.handleConsentsSubscribe();
      }
      sessionStorage.removeItem('subscribeLogin');
    }
  }

  componentWillLoad() {
    this.consentList = getConfig<BlogConfig>('sydle-ui-blog')?.consentListsByLanguage;
    this.individualSubscribe = getConfig<BlogConfig>('sydle-ui-blog')?.individualSubscribe;
    this.subscribeArray = sessionStorage.getItem('subscribeLogin')
      ? JSON.parse(sessionStorage.getItem('subscribeLogin') || '')
      : this.consentList?.map((el: { _id: string }) => el._id);
  }

  render() {
    if (!getConfig<BlogConfig>('sydle-ui-blog')?.enableNewsletter || !this.consentList) {
      return null;
    }
    return (
      <Host>
        <sy-blog-alert-modal />
        <div class="card">
          <div class="card-content">
            <div class="cta-text-content">
              <sy-text type="h4">{this.text}</sy-text>
              {this.individualSubscribe && (
                <div class="card-text-content-list">
                  {this.consentList.map((el: { identifier: string; _id: string }) => {
                    return (
                      <label htmlFor={`checkbox_${el._id}`} key={el._id}>
                        <sy-checkbox
                          checked={this.subscribeArray.includes(el._id)}
                          key={el._id}
                          value={el._id}
                          label={el.identifier}
                          onSyCheckboxDidChange={() => this.handleToggleCheckboxConsent(el._id)}
                        ></sy-checkbox>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <div class="cta-button">
              <sy-button
                icon="email"
                content="icon-right"
                variant="filled"
                color="secondary"
                label={this.buttonContent}
                onClick={() => this.handleConsentsSubscribe()}
              />
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
